import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Option, Select } from '@material-tailwind/react';
import { FaArrowLeft, FaCalendarAlt, FaChartBar, FaChartPie, FaFileInvoiceDollar, FaUsers } from 'react-icons/fa';
import { formatMoney } from '../shared/formatMoney';
import MonthlyInvoiceOverviewReport from './monthly-invoice-reports/MonthlyInvoiceOverviewReport';
import MonthlyInvoiceTrendReport from './monthly-invoice-reports/MonthlyInvoiceTrendReport';
import MonthlyInvoiceStatusReport from './monthly-invoice-reports/MonthlyInvoiceStatusReport';
import MonthlyInvoiceOwnersReport from './monthly-invoice-reports/MonthlyInvoiceOwnersReport';
import MonthlyInvoiceInvoicesReport from './monthly-invoice-reports/MonthlyInvoiceInvoicesReport';
import { useMonthlyInvoiceReports } from './monthly-invoice-reports/useMonthlyInvoiceReports';
import {
  formatDate,
  formatPeriod,
  normalizeInvoiceStatus,
  statusBadgeClass
} from './monthly-invoice-reports/helpers';
import ReportTabsBar from '../reports/shared/ReportTabsBar';

const TABS = [
  { id: 'overview', label: 'Overview', icon: FaChartBar },
  { id: 'monthly', label: 'Monthly Trend', icon: FaCalendarAlt },
  { id: 'status', label: 'Status & Payments', icon: FaChartPie },
  { id: 'owners', label: 'Owners', icon: FaUsers },
  { id: 'invoices', label: 'Invoices', icon: FaFileInvoiceDollar }
];

const STATUS_COLORS = { DRAFT: '#f59e0b', SENT: '#0ea5e9', PAID: '#10b981', REVERSED: '#ef4444' };
const PAYMENT_COLORS = { PAID: '#10b981', NOT_PAID: '#ef4444' };
const OWNER_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];
const MonthlyInvoiceReports = () => {
  const navigate = useNavigate();
  const {
    activeTab,
    setActiveTab,
    currentYear,
    trendYear,
    setTrendYear,
    reportYearOptions,
    summaryLoading,
    summaryError,
    summary,
    statusRows,
    paymentRows,
    statusChart,
    amountChart,
    paymentChart,
    trendLoading,
    trendError,
    trendSummary,
    trendChart,
    trendTopOwners,
    trendOwnerMatrix,
    trendMonthRows,
    ownerLoading,
    ownerError,
    ownerRows,
    ownerChart,
    invoiceLoading,
    invoiceError,
    invoiceRows,
    invoiceTotal,
    invoicePage,
    setInvoicePage,
    invoicePages,
    invoiceStart,
    invoiceEnd,
    invoiceDateRangeLabel,
    draftFilters,
    setDraftFilters,
    ownerOptions,
    applyFilters,
    resetFilters
  } = useMonthlyInvoiceReports();
  const activeTabMeta = TABS.find((tab) => tab.id === activeTab) || TABS[0];
  const ActiveTabIcon = activeTabMeta.icon;

  return (
    <div className="min-h-screen bg-slate-100 text-[13px] text-slate-800">
      <div className="mx-auto max-w-[1600px] px-4 py-4 sm:py-5 lg:px-6 lg:py-6">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate('/owners/invoices/monthly')} className="btn-back-dark">
              <FaArrowLeft />
              Back to Monthly Invoices
            </Button>
            <Button onClick={() => navigate('/reports/zero-invoices')} className="bg-orange-600 text-white shadow-none hover:bg-orange-500">
              <FaFileInvoiceDollar />
              Zero Invoice Report
            </Button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-800">
              <FaChartBar />
              Monthly Invoice Reports
            </div>
            <div className="w-full sm:w-56">
               <Select value={String(trendYear)} onChange={(value) => setTrendYear(Number(value) || currentYear)}>
                {reportYearOptions.map((option) => (
                  <Option key={option.value} value={String(option.value)}>{option.label}</Option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <ReportTabsBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

        <Card className="mb-4 shadow-lg">
          <CardBody className="py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <ActiveTabIcon />
              {activeTabMeta.label}
            </div>
          </CardBody>
        </Card>

        {summaryError && <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">{summaryError}</div>}
        {trendError && <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">{trendError}</div>}
        {ownerError && <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">{ownerError}</div>}
        {invoiceError && <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">{invoiceError}</div>}

        {activeTab === 'overview' && (
          <MonthlyInvoiceOverviewReport
            summaryLoading={summaryLoading}
            summary={summary}
            statusChart={statusChart}
            amountChart={amountChart}
            paymentChart={paymentChart}
            statusColors={STATUS_COLORS}
            paymentColors={PAYMENT_COLORS}
            formatMoney={formatMoney}
          />
        )}

        {activeTab === 'monthly' && (
          <MonthlyInvoiceTrendReport
            trendLoading={trendLoading}
            trendYear={trendYear}
            trendSummary={trendSummary}
            trendChart={trendChart}
            trendTopOwners={trendTopOwners}
            trendOwnerMatrix={trendOwnerMatrix}
            trendMonthRows={trendMonthRows}
            formatMoney={formatMoney}
          />
        )}

        {activeTab === 'status' && (
          <MonthlyInvoiceStatusReport
            summaryLoading={summaryLoading}
            statusRows={statusRows}
            paymentRows={paymentRows}
            statusChart={statusChart}
            paymentChart={paymentChart}
            statusColors={STATUS_COLORS}
            paymentColors={PAYMENT_COLORS}
            statusBadgeClass={statusBadgeClass}
            formatMoney={formatMoney}
          />
        )}

        {activeTab === 'owners' && (
          <MonthlyInvoiceOwnersReport
            ownerLoading={ownerLoading}
            ownerChart={ownerChart}
            ownerRows={ownerRows}
            ownerColors={OWNER_COLORS}
            formatMoney={formatMoney}
          />
        )}

        {activeTab === 'invoices' && (
          <MonthlyInvoiceInvoicesReport
            invoiceLoading={invoiceLoading}
            invoiceRows={invoiceRows}
            invoiceTotal={invoiceTotal}
            invoicePage={invoicePage}
            setInvoicePage={setInvoicePage}
            invoicePages={invoicePages}
            invoiceStart={invoiceStart}
            invoiceEnd={invoiceEnd}
            invoiceDateRangeLabel={invoiceDateRangeLabel}
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            ownerOptions={ownerOptions}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            normalizeInvoiceStatus={normalizeInvoiceStatus}
            formatDate={formatDate}
            formatPeriod={formatPeriod}
            statusBadgeClass={statusBadgeClass}
            formatMoney={formatMoney}
          />
        )}
      </div>
    </div>
  );
};

export default MonthlyInvoiceReports;
